import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import Sidebar from '../components/marketplace/Sidebar';
import ProductCard from '../components/marketplace/ProductCard';
import CartView from '../components/marketplace/CartView';
import WishlistView from '../components/marketplace/WishlistView';
import OrdersView from '../components/marketplace/OrdersView';
import catalogue, { CATEGORIES } from '../config/catalogue';
import { LayoutGrid, List, Search, LogIn } from 'lucide-react';

const MarketplaceApp = () => {
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embedded') === 'true';
  const { isLoggedIn, login } = useAuth();
  const { cartCount } = useCart();
  const initialCategory = CATEGORIES.includes(searchParams.get('category')) ? searchParams.get('category') : 'All';
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [activeView, setActiveView] = useState('browse');
  const [layout, setLayout] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredProducts = useMemo(() => {
    let items = catalogue;
    if (activeCategory !== 'All') {
      items = items.filter((p) => p.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
      );
    }
    return items;
  }, [activeCategory, searchQuery]);

  // Login gate
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-page">
        {!isEmbedded && <Header />}
        <div className={`flex items-center justify-center ${isEmbedded ? 'min-h-screen' : 'min-h-[calc(100vh-73px)]'}`}>
          <div className="text-center space-y-6 max-w-md px-6">
            <div className="w-20 h-20 rounded-2xl mx-auto flex items-center justify-center" style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)' }}>
              <LogIn size={36} style={{ color: '#F97316' }} />
            </div>
            <h1 className="text-3xl font-bold text-foreground-primary">Sign in to Marketplace</h1>
            <p className="text-foreground-secondary">
              Log in with your Vinayak Robotics account to browse, purchase, and manage marketplace items.
            </p>
            <button
              onClick={login}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-semibold transition-smooth"
              style={{ background: '#F97316', color: '#0B0E13' }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 30px rgba(249,115,22,0.5)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
            >
              <LogIn size={18} />
              Sign in with Authentik
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page">
      {!isEmbedded && <Header />}
      <div className="flex" style={isEmbedded ? {} : { marginTop: '73px' }}>
        <Sidebar
          categories={CATEGORIES}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
          activeView={activeView}
          onViewChange={setActiveView}
          cartCount={cartCount}
        />

        <main className="flex-1 p-6 min-h-[calc(100vh-73px)]">
          {activeView === 'cart' ? (
            <CartView />
          ) : activeView === 'wishlist' ? (
            <WishlistView layout={layout} />
          ) : activeView === 'orders' ? (
            <OrdersView />
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-6 gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
                    <input
                      type="text"
                      placeholder="Search marketplace..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 rounded-lg text-sm bg-subtle text-foreground-primary border border-subtle focus:outline-none focus:border-orange-500/50 transition-smooth placeholder:text-foreground-muted"
                    />
                  </div>
                  <span className="text-xs text-foreground-muted">{filteredProducts.length} items</span>
                </div>
                <div className="flex items-center gap-1 glass-effect rounded-lg p-0.5">
                  <button
                    onClick={() => setLayout('grid')}
                    className={`p-1.5 rounded transition-smooth ${layout === 'grid' ? 'text-foreground-primary' : 'text-foreground-muted'}`}
                    style={layout === 'grid' ? { background: 'rgba(249,115,22,0.15)', color: '#F97316' } : {}}
                  >
                    <LayoutGrid size={16} />
                  </button>
                  <button
                    onClick={() => setLayout('list')}
                    className={`p-1.5 rounded transition-smooth ${layout === 'list' ? 'text-foreground-primary' : 'text-foreground-muted'}`}
                    style={layout === 'list' ? { background: 'rgba(249,115,22,0.15)', color: '#F97316' } : {}}
                  >
                    <List size={16} />
                  </button>
                </div>
              </div>

              {/* Products */}
              {filteredProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 space-y-3">
                  <p className="text-foreground-muted text-lg">No items found</p>
                  <p className="text-sm text-foreground-secondary">Try a different search or category.</p>
                </div>
              ) : layout === 'grid' ? (
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredProducts.map((p) => <ProductCard key={p.id} product={p} layout="grid" />)}
                </div>
              ) : (
                <div>
                  {filteredProducts.map((p) => <ProductCard key={p.id} product={p} layout="list" />)}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default MarketplaceApp;
